<section id="pricing" #pricingAnchor class="py-24 bg-background">
  <div class="container mx-auto px-4">
    <div *ngIf="showSectionHeader" class="max-w-3xl mx-auto text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4">
        {{ useCampaignCopy ? c.pricing.title : t.pricing.title }}
      </h2>
      <p class="text-lg text-muted-foreground">
        {{ useCampaignCopy ? c.pricing.subtitle : t.pricing.subtitle }}
      </p>
    </div>

    <div *ngIf="plansLoading" class="flex justify-center py-16">
      <div class="flex flex-col items-center gap-4 text-muted-foreground">
        <i class="fa fa-spinner fa-spin text-3xl text-primary"></i>
        <span class="text-sm">{{ landingLang === 'ar' ? 'جاري تحميل الباقات…' : landingLang === 'he' ? 'טוען תוכניות…' : 'Loading plans…' }}</span>
      </div>
    </div>

    <div *ngIf="plansError && !plansLoading" class="max-w-lg mx-auto text-center py-12 rounded-2xl border border-border bg-card px-6">
      <p class="text-muted-foreground mb-6">
        {{ landingLang === 'ar' ? 'تعذّر تحميل الباقات.' : landingLang === 'he' ? 'לא ניתן לטעון חבילות.' : 'Could not load plans.' }}
      </p>
      <button type="button" (click)="reloadPlans()" class="thi-btn-outline-lg inline-flex justify-center">
        {{ landingLang === 'ar' ? 'إعادة المحاولة' : landingLang === 'he' ? 'נסה שוב' : 'Retry' }}
      </button>
    </div>

    <div
      *ngIf="!plansLoading && !plansError && plans.length > 0"
      class="grid gap-8 max-w-6xl mx-auto items-start [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]"
    >
      <div
        *ngFor="let plan of plans; let i = index; trackBy: trackByPlanId"
        class="relative p-8 rounded-2xl transition-all duration-300"
        [ngClass]="
          isPopular(i)
            ? 'thi-surface-gradient-pricing border-2 border-primary shadow-xl shadow-primary/20 md:scale-105'
            : 'bg-card border border-border hover:border-primary/30'
        "
      >
        <div *ngIf="isPopular(i)" class="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
          <span class="bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full">
            {{ t.pricing.popular }}
          </span>
        </div>
        <div class="text-center mb-8">
          <h3 class="text-xl font-bold text-foreground mb-2">{{ planName(plan) }}</h3>
          <p *ngIf="planDesc(plan) && planFeatureLines(plan).length <= 1" class="text-muted-foreground text-sm mb-4">
            {{ planDesc(plan) }}
          </p>
          <div class="text-4xl font-bold text-foreground">{{ formatPrice(plan) }}</div>
        </div>
        <ul *ngIf="planFeatureLines(plan).length > 1" class="space-y-4 mb-8">
          <li *ngFor="let f of planFeatureLines(plan)" class="flex items-start gap-3">
            <span class="w-5 h-5 rounded-full bg-success/20 text-success flex items-center justify-center flex-shrink-0 mt-0.5">
              <i class="fa fa-check text-xs"></i>
            </span>
            <span class="text-muted-foreground text-sm">{{ f }}</span>
          </li>
        </ul>
        <a
          [routerLink]="registerLink"
          [queryParams]="registerParams(plan.id)"
          (click)="onPlanClick(plan.id)"
          [class.thi-btn-outline-lg]="!isPopular(i)"
          [class.thi-btn-default]="isPopular(i)"
          class="w-full justify-center inline-flex shadow-md"
        >
          {{ t.nav.startFree }}
        </a>
      </div>
    </div>

    <p *ngIf="!plansLoading && !plansError && plans.length > 0" class="text-center text-muted-foreground mt-8 max-w-2xl mx-auto text-sm">
      {{ t.pricing.aiNote }}
    </p>
  </div>
</section>

<section
  *ngIf="!plansLoading && !plansError && plans.length > 0"
  class="py-24 bg-secondary/30"
  id="pricing-compare"
>
  <div class="container mx-auto px-4">
    <div class="text-center mb-12">
      <h2 class="text-3xl font-bold text-foreground mb-3">
        {{ useCampaignCopy ? c.pricing.compareTitle : t.pricing.comparePlansTitle }}
      </h2>
      <p *ngIf="useCampaignCopy" class="text-muted-foreground">{{ c.pricing.compareSubtitle }}</p>
    </div>

    <!-- Mobile tabs: one plan at a time (campaign) -->
    <div *ngIf="mobileComparisonTabs" class="thi-compare-mobile">
      <div class="flex gap-2 mb-6 overflow-x-auto pb-1">
        <button
          type="button"
          *ngFor="let plan of plans; let i = index"
          (click)="selectedComparisonIndex = i"
          class="flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
          [ngClass]="
            selectedComparisonIndex === i
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-card text-foreground border-border'
          "
        >
          {{ planName(plan) }}
        </button>
      </div>
      <div class="bg-card border border-border rounded-2xl divide-y divide-border">
        <div *ngFor="let row of comparisonMetricRows" class="flex justify-between gap-4 px-5 py-4">
          <span class="font-medium text-foreground text-sm">{{ row.label }}</span>
          <span class="text-muted-foreground text-sm tabular-nums">{{ row.cells[selectedComparisonIndex] }}</span>
        </div>
      </div>
    </div>

    <!-- Desktop table: columns = live packages from API -->
    <div
      class="thi-pricing-table-wrap max-w-6xl mx-auto overflow-x-auto"
      [class.thi-compare-desktop]="mobileComparisonTabs"
    >
      <table class="w-full bg-card rounded-2xl border border-border overflow-hidden text-sm min-w-[520px]">
        <thead>
          <tr class="border-b border-border bg-secondary/50">
            <th class="p-4 text-start font-semibold text-foreground">
              {{ useCampaignCopy ? c.pricing.featureCol : (landingLang === 'ar' ? 'الميزة' : landingLang === 'he' ? 'תכונה' : 'Feature') }}
            </th>
            <th
              *ngFor="let plan of plans; let i = index"
              class="p-4 text-center font-semibold"
              [class.text-primary]="isPopular(i)"
              [class.text-foreground]="!isPopular(i)"
            >
              {{ planName(plan) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of comparisonMetricRows" class="border-b border-border last:border-0">
            <td class="p-4 font-medium text-foreground">{{ row.label }}</td>
            <td
              *ngFor="let cell of row.cells; let i = index"
              class="p-4 text-center text-muted-foreground"
              [ngClass]="{ 'bg-primary/5': isPopular(i) }"
            >
              {{ cell }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</section>
